import { createContext, useContext, useEffect, useState } from 'react'

type ColorScheme = 'light' | 'dark'

interface ColorSchemeContextType {
    scheme: ColorScheme | 'system'
    resolvedScheme: ColorScheme
    setScheme: (scheme: ColorScheme | 'system') => void
}

const ColorSchemeContext = createContext<ColorSchemeContextType>({
    scheme: 'system',
    resolvedScheme: 'light',
    setScheme: () => {},
})

export const useColorScheme = () => useContext(ColorSchemeContext)

function getSystemScheme(): ColorScheme {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredScheme(): ColorScheme | 'system' {
    if (typeof window === 'undefined') return 'system'
    return (localStorage.getItem('color-scheme') as ColorScheme | 'system') ?? 'system'
}

export function ColorSchemeProvider({ children }: { children: React.ReactNode }) {
    const [scheme, setSchemeState] = useState<ColorScheme | 'system'>(getStoredScheme)
    const [systemScheme, setSystemScheme] = useState<ColorScheme>(getSystemScheme)

    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)')
        const handler = (e: MediaQueryListEvent) => setSystemScheme(e.matches ? 'dark' : 'light')
        mq.addEventListener('change', handler)
        return () => mq.removeEventListener('change', handler)
    }, [])

    const setScheme = (newScheme: ColorScheme | 'system') => {
        setSchemeState(newScheme)
        localStorage.setItem('color-scheme', newScheme)
    }

    const resolvedScheme = scheme === 'system' ? systemScheme : scheme

    return (
        <ColorSchemeContext.Provider value={{ scheme, resolvedScheme, setScheme }}>
            {children}
        </ColorSchemeContext.Provider>
    )
}
